import React, { useMemo, useState } from "react";
import { FiChevronDown } from "react-icons/fi";
import {
  Body,
  CardContainer,
  Collapse,
  CollapseClip,
  CollapseContent,
  Description,
  DiscountBadge,
  Image,
  ImageContainer,
  NewPrice,
  OldPrice,
  PriceRange,
  ProductVariant,
  SizeChip,
  SizeList,
  Summary,
  Title,
  ToggleButton,
  VariantImage,
  VariantPrice,
  VariantSize,
  VariantsTitle,
} from "./ProductCardElements";
import { isDateInFuture, sanityImage } from "../../../utils";
import { useLanguage } from "../../../contexts/LanguageContext";

const formatPrice = (price) => `${price} RON`;

const getEffectivePrice = (size) =>
  Number(isDateInFuture(size.validUntil) ? size.discount : size.price);

const getPriceRange = (sizes) => {
  const prices = sizes
    .map(getEffectivePrice)
    .filter((price) => Number.isFinite(price) && price > 0);
  if (prices.length === 0) return null;
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  return min === max ? formatPrice(min) : `${min} – ${max} RON`;
};

const ProductCard = ({ product }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { t } = useLanguage();
  const { _id, name, description } = product;
  const sizes = useMemo(() => product.sizes || [], [product.sizes]);

  const sortedSizes = useMemo(
    () => [...sizes].sort((a, b) => a.size - b.size),
    [sizes]
  );
  const hasDiscount = sizes.some((size) => isDateInFuture(size.validUntil));
  const priceRange = getPriceRange(sizes);
  const detailsId = `product-${_id}-details`;

  return (
    <CardContainer $expanded={isExpanded}>
      <Summary onClick={() => setIsExpanded((expanded) => !expanded)}>
        <ImageContainer>
          <Image src={sanityImage(sizes[0]?.imageURL, 800)} alt={name} loading="lazy" />
          {hasDiscount && <DiscountBadge>{t("PRODUCTS.DISCOUNT")}</DiscountBadge>}
        </ImageContainer>
        <Body>
          <Title>{name}</Title>
          <Description $expanded={isExpanded}>{description}</Description>
          <SizeList>
            {sortedSizes.map((size, index) => (
              <SizeChip key={size._id || index}>
                {size.size}
                {size.unit}
              </SizeChip>
            ))}
          </SizeList>
          {priceRange && <PriceRange>{priceRange}</PriceRange>}
          <ToggleButton
            type="button"
            $expanded={isExpanded}
            aria-expanded={isExpanded}
            aria-controls={detailsId}
          >
            {isExpanded ? t("PRODUCTS.CLOSE") : t("PRODUCTS.VIEW_DETAILS")}
            <FiChevronDown />
          </ToggleButton>
        </Body>
      </Summary>

      <Collapse id={detailsId} $open={isExpanded} aria-hidden={!isExpanded}>
        <CollapseClip>
          <CollapseContent>
            <VariantsTitle>{t("PRODUCTS.AVAILABLE_SIZES")}</VariantsTitle>
            {sortedSizes.map((size, index) => {
              const isDiscounted = isDateInFuture(size.validUntil);
              return (
                <ProductVariant
                  key={size._id || index}
                  $index={index}
                  $open={isExpanded}
                >
                  <VariantImage
                    src={sanityImage(size.imageURL, 160)}
                    alt={`${name} - ${size.size}${size.unit}`}
                    loading="lazy"
                  />
                  <VariantSize>
                    {size.size}
                    {size.unit}
                  </VariantSize>
                  <VariantPrice>
                    {isDiscounted ? (
                      <>
                        <OldPrice>{formatPrice(size.price)}</OldPrice>
                        <NewPrice>{formatPrice(size.discount)}</NewPrice>
                      </>
                    ) : (
                      formatPrice(size.price)
                    )}
                  </VariantPrice>
                </ProductVariant>
              );
            })}
          </CollapseContent>
        </CollapseClip>
      </Collapse>
    </CardContainer>
  );
};

export default ProductCard;
